import { useQuery } from '@tanstack/react-query';

import { api } from '@shared/api/client';
import { key, STALE_TIME } from '@shared/api/query-keys';

import type { Page, Plan } from './types';

/**
 * The plan set, as the operator defines it.
 *
 * **It lives in the area's `_shared/` because two routes read it, not because it
 * is generic.** `/plans` manages the registry; `/subscriptions` offers the same
 * rows as the ledger's plan facet, and a facet listing plan names the money
 * service does not have would offer the operator a narrowing that returns
 * nothing. Two copies of one read is how the two lists start disagreeing about
 * which plans exist, and a feature reaching into a sibling feature's `queries.ts`
 * is the import the layout standard exists to forbid — `_shared/` is a feature's
 * public sub-surface and this is what it is for.
 *
 * The key mirrors the REST URL, so every mutation on the registry — and the
 * facet two routes away — invalidates the exact tuple this query registered
 * rather than a name invented per feature.
 *
 * `staleTime` is the list default: a plan changes rarely, but an operator
 * returning to the tab after editing one in another window wants the current
 * set rather than a cached one.
 */
export function plansKey() {
  return key('finance', 'plans');
}

export function usePlans() {
  return useQuery({
    queryKey: plansKey(),
    queryFn: () => api.get<Page<Plan>>('finance', '/admin/plans'),
    staleTime: STALE_TIME.list,
  });
}
